import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore.js';
import { useFeatures } from '../../stores/brandingStore.js';
import { PRIMARY, hasAdminAccess, isAdminPath } from './navItems.js';
import { featureOn } from './navVisibility.js';

/** personal = the worker's own workspace · admin = the back office ·
 *  combined = a management-only console, which has no split to make. */
export type ShellMode = 'personal' | 'admin' | 'combined';

export interface ShellSurface {
  mode: ShellMode;
  /** The deployment offers self-service AND this login can use it. */
  selfService: boolean;
  canAdmin: boolean;
  /**
   * IS THIS THE PHONE-SHAPED SURFACE?
   *
   * Only the worker's own workspace is. The back office keeps its docked rail
   * at every width — no off-canvas drawer, no hamburger, no bottom bar — on the
   * owner's call: an admin who narrows a desktop window wants the console they
   * were using, not a phone layout, and being handed a hamburger for a rail
   * that was on screen a moment ago is a worse trade than a narrower column.
   *
   * It is the same fact four things need (the rail's off-canvas classes, the
   * shell's menu button and scrim, the bottom bar's whole existence, and the
   * band the shell reserves for it), so it is decided once here rather than
   * four times from the route.
   *
   * THE RESERVED BAND is the least obvious of those, so it is written down
   * here. `MobileBottomNav` floats over the page, and a list scrolled BEHIND
   * it: the END of a scroll was clear — the footer carried 96px for exactly
   * that — but every row on the way past disappeared under the capsule and came
   * out the other side. Clearance at the end of a scroll does nothing for the
   * middle of one. `AppShell` pads ITSELF by the capsule's footprint when this
   * is true, which shortens the scrolling column rather than adding space
   * inside it, and nothing passes underneath any more. Reserving it on a
   * desktop, or on the back office where the rail is docked, would be 96px of
   * dead air under every page — hence the flag rather than a media query alone.
   */
  phoneShaped: boolean;
}

/**
 * Which surface the shell is showing, from the route and the login.
 *
 * Self-service deployments split into a personal "My workspace" and a separate
 * "Admin panel", derived from the ROUTE so bookmarks and deep links land in the
 * right one. A management-only console has no such split. Both conditions must
 * hold for the split to exist: the deployment offers self-service AND this user
 * has a personal record to point it at — without the second, an admin-only
 * login landed in a "My workspace" with nothing in it and had to find "Admin
 * panel" to get anywhere.
 */
/**
 * PAGES BOTH SURFACES OPEN. The bell sits in both bars, so its "View all"
 * page belongs to whichever surface it was opened from: an admin reading
 * notifications stays in the back office (owner, 2026-10-14: "view all
 * notification takes you as user when you check as admin"). Kept per tab,
 * in memory — a fresh load lands on the personal default.
 */
export const SHARED_PATHS: readonly string[] = ['/notifications'];
let lastSurface: 'admin' | 'personal' = 'personal';

export function useShellSurface(): ShellSurface {
  const { pathname } = useLocation();
  const role = useAuthStore((s) => s.user?.role);
  const permissions = useAuthStore((s) => s.user?.permissions);
  const hasEmployeeRecord = useAuthStore((s) => Boolean(s.user?.employeeId));
  const features = useFeatures();

  const selfService = featureOn(PRIMARY, features) && hasEmployeeRecord;
  const canAdmin = hasAdminAccess(role, features, permissions);
  const shared = SHARED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const surface = shared ? (canAdmin ? lastSurface : 'personal') : isAdminPath(pathname) ? 'admin' : 'personal';
  if (!shared) lastSurface = surface;
  const mode: ShellMode = !selfService ? 'combined' : surface;

  return {
    mode,
    selfService,
    canAdmin,
    phoneShaped: mode === 'personal',
  };
}
